import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Box,
  Snackbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  LinearProgress,
  Tooltip,
  Autocomplete
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ContextSession } from "../../services/ContextSession";

const CONSTANTS = {
  ALLOWED_FILE_TYPES: [
    'application/pdf', 
    'application/msword', 
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg', 
    'image/png'
  ],
  MAX_FILE_SIZE: 10 * 1024 * 1024 // 10MB
};

const CrearDemanda = () => {
  const [apiData, setApiData] = useState({
    fields: [],
    rules: [],
    catalogs: {}
  });
  const [procuradores, setProcuradores] = useState([]);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState({
    api: true,
    procuradores: false,
    submitting: false
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [attachmentMetadata, setAttachmentMetadata] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [lockedFields, setLockedFields] = useState({});
  const { sessionData } = useContext(ContextSession);
  let sessi = useContext(ContextSession);

  // Estados para el autocomplete
  const [participantes, setParticipantes] = useState([]);
  const [loadingParticipantes, setLoadingParticipantes] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // IDs constantes para validaciones
  const CONSTANT_IDS = {
    NATURALEZA: {
      A_FAVOR: 1,
      EN_CONTRA: 2
    },
    TIPO_DEMANDANTE: {
      OTROS: 5
    },
    JUZGADO: {
      OTROS: 5 
    },
    TIPO_DEMANDA: {
      EJECUCION_GARANTIAS: 2,
      EJECUCION_PAGOS: 3,
      EJECUCION_TITULOS: 4,
      EJECUTIVA: 5
    },
    MONEDA: {
      LEMPIRAS: 1
    },
    RIESGO_CREDITO: {
      SI: 1, 
      NO: 2 
    },
    SUCURSAL: {
      CORTES: 2 
    }
  };

  // Opciones para responsablevisiblepara
  const OPCIONES_VISIBILIDAD = [
    { id: 1, label: 'Secretaria Legal' },
    { id: 2, label: 'Recursos Humanos' },
  ];

  // Campos que deben estar ocultos pero enviarse en el formulario
  const CAMPOS_OCULTOS = ['numerodemanda', 'estado', 'sucursal','procuradorasignado'] ;

  // Tooltips personalizados para cada campo
  const fieldTooltips = {
    numerodemanda: "Número único asignado por la institución para cada caso. Formato: incremental-año (ej: 45-2024). Asignado automáticamente por el sistema.",
    numeroexpediente: "Número asignado por el juzgado para identificar el expediente judicial.",
    fechapresentacion: "Fecha en que se presentó formalmente la demanda ante el juzgado.",
    naturaleza: "Indica si la institución es demandante (A Favor) o demandada (En Contra). Esto afecta qué campos estarán disponibles.",
    nombredemandado: "Nombre completo de la persona natural o jurídica contra quien se presenta la demanda. Solo aplica para demandas A Favor. Use el autocomplete para buscar en el registro de partes.",
    nombredemandante: "Nombre completo de la persona natural o jurídica que demanda a la institución. Solo aplica para demandas En Contra. Use el autocomplete para buscar en el registro de partes.",
    tipodemandante: "Clasificación del tipo de persona que presenta la demanda. Si selecciona 'Otros', debe especificar en Observaciones.",
    tipodemanda: "Tipo legal de la demanda según clasificación jurídica. Algunos tipos activan automáticamente la asociación a riesgo de crédito.",
    motivodemanda: "Descripción detallada de los hechos y fundamentos legales que dan origen a la demanda.",
    juzgado: "Órgano judicial donde se presentó la demanda. Si selecciona 'Otros', debe especificar en Observaciones.",
    moneda: "Moneda en la que está expresado el monto demandado. Si es Lempiras, el tipo de cambio se fija en 1.0000 automáticamente.",
    tipocambio: "Factor de conversión monetaria. Para moneda nacional es 1.0000, para extranjera use el tipo de cambio oficial del BCH.",
    montoestimacion: "Monto de estimación constituido para demandas A Favor. Para demandas En Contra este campo se bloquea en 0.00.",
    cuentaestimacion: "Cuenta contable donde se registra la estimación. Obligatoria cuando el monto de estimación es mayor a 0.",
    cuentaprovision: "Cuenta contable donde se registra la provisión. Obligatoria cuando el monto de provisión es mayor a 0.",
    cuentagastos: "Cuenta contable para gastos legales. Obligatoria cuando el monto de gastos legales es mayor a 0.",
    riesgocredito: "Indica si la demanda está relacionada con operaciones de crédito. Se activa automáticamente para ciertos tipos de demanda.",
    observaciones: "Información adicional relevante. Obligatorio cuando se selecciona 'Otros' en Tipo de Demandante o Juzgado.",
    procuradorasignado: "Nombre del procurador o abogado asignado para llevar el caso.",
    usuarioprocuradorid: "Procurador asignado para llevar el caso. Al seleccionar, se completará automáticamente el nombre del procurador.",
    fechamaximacontestacion: "Fecha límite establecida por el juzgado para presentar la contestación a la demanda.",
    accioninstitucion: "Acciones o medidas que ha tomado o planea tomar la institución respecto a la demanda.",
    estado: "Estado actual del trámite de la demanda dentro del sistema.",
    responsablevisiblepara: "Define quién puede ver esta demanda en el sistema. "
  };

  // Buscar participantes con debounce
  useEffect(() => {
    const timerId = setTimeout(() => {
      if (searchTerm.trim() !== '') {
        fetchParticipantes(searchTerm);
      }
    }, 500);

    return () => clearTimeout(timerId);
  }, [searchTerm]);

  const fetchParticipantes = async (term = '') => {
    setLoadingParticipantes(true);
    try {
      const token = sessionData.token;
      const response = await fetch(`https://bei360.injupemp.gob.hn:8444/api/partes/listar/?search=${term}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      if (!response.ok) throw new Error('Error al cargar participantes');
      const data = await response.json();
      setParticipantes(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingParticipantes(false);
    }
  };

  // Cargar datos de la API de secretaría
  useEffect(() => {
    const fetchApiData = async () => {
      try {
        const token = sessionData.token;
        const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/tramites/campos/secretaria', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) throw new Error('Error al cargar configuración de campos');

        const data = await response.json();
        
        const updatedData = {
          ...data,
          fields: [
            ...data.fields,
          ]
        };

        setApiData(updatedData);

        // Inicializar formData con valores por defecto
        const initialData = {};
        const initialLocked = {};
        
        updatedData.fields.forEach(field => {
          if (field.default === 'now') {
            if (field.type === 'datetime') {
              initialData[field.name] = new Date().toISOString();
            } else if (field.type === 'date') {
              initialData[field.name] = new Date().toISOString().split('T')[0];
            }
          } else if (field.default) {
            initialData[field.name] = field.default;
          } else {
            initialData[field.name] = '';
          }

          if (field.ui?.widget === 'hidden') {
            initialLocked[field.name] = true;
          }
        });

        // Establecer valores automáticos del usuario
        initialData.usuariocreadorid = sessionData.id;
        initialLocked.usuariocreadorid = true;
        
        // VALORES PREDETERMINADOS PARA CAMPOS OCULTOS
        initialData.sucursal = CONSTANT_IDS.SUCURSAL.CORTES.toString();
        initialLocked.sucursal = true;
        initialData.montoestimacion = '0.00';
        initialLocked.montoestimacion = false;
        initialData.moneda = CONSTANT_IDS.MONEDA.LEMPIRAS.toString();
        initialData.estado = 'Creada'; // Estado predeterminado
        initialData.numerodemanda = ''; // Se asignará automáticamente o se dejará vacío
        
        // Establecer valor por defecto para responsablevisiblepara
        initialData.responsablevisiblepara = '1'; // Por defecto: Usuario Creador

        setFormData(initialData);
        setLockedFields(initialLocked);
        setLoading(prev => ({ ...prev, api: false }));

      } catch (err) {
        setError(err.message);
        setLoading(prev => ({ ...prev, api: false }));
      }
    };

    // Cargar procuradores
    const fetchProcuradores = async () => {
      try {
        setLoading(prev => ({ ...prev, procuradores: true }));
        const token = sessionData.token;
        const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/auth/oficialesLegal/', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) throw new Error('Error al cargar lista de procuradores');

        const data = await response.json();
        setProcuradores(data);
      } catch (err) {
        console.error('Error al cargar procuradores:', err);
        setError('Error al cargar lista de procuradores');
      } finally {
        setLoading(prev => ({ ...prev, procuradores: false }));
      }
    };

    fetchApiData();
    fetchProcuradores();
  }, [sessionData]);

  // Aplicar reglas cuando cambian los datos del formulario
  useEffect(() => {
    applyRules();
  }, [formData]);

  // Actualizar procuradorasignado cuando cambia usuarioprocuradorid
  useEffect(() => {
    if (formData.usuarioprocuradorid) {
      const procuradorSeleccionado = procuradores.find(p => p.id === parseInt(formData.usuarioprocuradorid));
      if (procuradorSeleccionado) {
        setFormData(prev => ({
          ...prev,
          procuradorasignado: procuradorSeleccionado.nombre
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        procuradorasignado: ''
      }));
    }
  }, [formData.usuarioprocuradorid, procuradores]);

  const applyRules = () => {
    const newLockedFields = { ...lockedFields };
    const newFormData = { ...formData };
    let hasChanges = false;

    // Aplicar reglas de la API
    apiData.rules.forEach(rule => {
      let conditionMet = false;
      
      if (rule.if || rule.if_not) {
        const conditionField = rule.if?.field || rule.if_not?.field;
        const currentValue = formData[conditionField];
        const isIfNot = !!rule.if_not;
        
        if (rule.if?.id_equals !== undefined || rule.if_not?.id_equals !== undefined) {
          const conditionValue = rule.if?.id_equals || rule.if_not?.id_equals;
          conditionMet = isIfNot 
            ? currentValue != conditionValue
            : currentValue == conditionValue;
        }
        else if (rule.if?.id_in) {
          conditionMet = rule.if.id_in.includes(parseInt(currentValue));
        }
        else if (rule.if?.greater_than !== undefined) {
          const numericValue = parseFloat(currentValue) || 0;
          conditionMet = numericValue > rule.if.greater_than;
        }
      }

      if (conditionMet) {
        if (rule.then.set) {
          Object.entries(rule.then.set).forEach(([field, value]) => {
            if (newFormData[field] !== value) {
              newFormData[field] = value;
              hasChanges = true;
            }
          });
        }

        if (rule.then.lock) {
          rule.then.lock.forEach(field => {
            newLockedFields[field] = true;
          });
        }

        if (rule.then.clear) {
          rule.then.clear.forEach(field => {
            if (newFormData[field] !== '') {
              newFormData[field] = '';
              hasChanges = true;
            }
          });
        }
      } else {
        if (rule.then.lock) {
          rule.then.lock.forEach(field => {
            const fieldConfig = apiData.fields.find(f => f.name === field);
            if (fieldConfig && fieldConfig.ui?.widget !== 'hidden') {
              delete newLockedFields[field];
            }
          });
        }
      }
    });

    // REGLAS ADICIONALES SEGÚN ESPECIFICACIONES

    // 1. Regla de Naturaleza de Demanda
    if (parseInt(newFormData.naturaleza) === CONSTANT_IDS.NATURALEZA.A_FAVOR) {
      if (newFormData.nombredemandante !== '') {
        newFormData.nombredemandante = '';
        hasChanges = true;
      }
      newLockedFields.nombredemandante = true;
      delete newLockedFields.nombredemandado;
      delete newLockedFields.montoestimacion;
      
    } else if (parseInt(newFormData.naturaleza) === CONSTANT_IDS.NATURALEZA.EN_CONTRA) {
      if (newFormData.nombredemandado !== '') {
        newFormData.nombredemandado = '';
        hasChanges = true;
      }
      newLockedFields.nombredemandado = true;
      delete newLockedFields.nombredemandante;
      newLockedFields.montoestimacion = true;
      if (newFormData.montoestimacion !== '0.00') {
        newFormData.montoestimacion = '0.00';
        hasChanges = true;
      }
    }

    // 2. Regla de Tipo de Demanda - Riesgo de Crédito
    const tiposDemandaRiesgoCredito = [
      CONSTANT_IDS.TIPO_DEMANDA.EJECUCION_GARANTIAS,
      CONSTANT_IDS.TIPO_DEMANDA.EJECUCION_PAGOS,
      CONSTANT_IDS.TIPO_DEMANDA.EJECUCION_TITULOS,
      CONSTANT_IDS.TIPO_DEMANDA.EJECUTIVA
    ];

    if (tiposDemandaRiesgoCredito.includes(parseInt(newFormData.tipodemanda))) {
      if (newFormData.riesgocredito !== CONSTANT_IDS.RIESGO_CREDITO.SI.toString()) {
        newFormData.riesgocredito = CONSTANT_IDS.RIESGO_CREDITO.SI.toString();
        hasChanges = true;
      }
      newLockedFields.riesgocredito = true;
    } else {
      delete newLockedFields.riesgocredito;
    }

    // 3. Regla de Moneda - Tipo de Cambio
    if (parseInt(newFormData.moneda) === CONSTANT_IDS.MONEDA.LEMPIRAS) {
      if (newFormData.tipocambio !== '1.0000') {
        newFormData.tipocambio = '1.0000';
        hasChanges = true;
      }
      newLockedFields.tipocambio = true;
    } else {
      delete newLockedFields.tipocambio;
    }

    if (hasChanges) {
      setFormData(newFormData);
    }
    setLockedFields(newLockedFields);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleAutocompleteChange = (event, newValue, fieldName) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: typeof newValue === 'string' ? newValue : newValue?.NombreParte || '',
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    const validFiles = files.filter(file => {
      if (file.size > CONSTANTS.MAX_FILE_SIZE) {
        setError(`El archivo ${file.name} excede el tamaño máximo de ${CONSTANTS.MAX_FILE_SIZE / (1024 * 1024)}MB`);
        return false;
      }
      if (!CONSTANTS.ALLOWED_FILE_TYPES.includes(file.type)) {
        setError(`Tipo de archivo no permitido: ${file.name}`);
        return false;
      }
      return true;
    });

    const newAttachments = validFiles.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      file
    }));

    setAttachmentMetadata([...attachmentMetadata, ...newAttachments]);
  };

  const handleRemoveAttachment = (index) => {
    const updatedAttachments = [...attachmentMetadata];
    updatedAttachments.splice(index, 1);
    setAttachmentMetadata(updatedAttachments);
  };

  const validarCampos = () => {
    const errores = {};
    
    // Validar campos requeridos básicos (excluyendo campos ocultos)
    apiData.fields.forEach(field => {
      if (CAMPOS_OCULTOS.includes(field.name)) return; // No validar campos ocultos
      
      const value = formData[field.name];
      const isLocked = lockedFields[field.name];
      
      if (field.required && !isLocked && (!value || value.toString().trim() === '')) {
        errores[field.name] = `${field.ui?.label || field.name} es requerido`;
      }

      if (field.maxLength && value && value.length > field.maxLength) {
        errores[field.name] = `Máximo ${field.maxLength} caracteres`;
      }

      if (field.min !== undefined && value !== '' && parseFloat(value) < field.min) {
        errores[field.name] = `El valor mínimo es ${field.min}`;
      }
    });

    // 1. Validación de formato de número de demanda (solo si está presente)
    if (formData.numerodemanda && formData.numerodemanda.trim() !== '') {
      const regexNumeroDemanda = /^\d+-\d{4}$/;
      if (!regexNumeroDemanda.test(formData.numerodemanda)) {
        errores.numerodemanda = 'Formato inválido. Use: incremental-año (ej: 45-2024)';
      }
    }

    // 2. Validación de Tipo de Demandante = Otros
    if (parseInt(formData.tipodemandante) === CONSTANT_IDS.TIPO_DEMANDANTE.OTROS) {
      if (!formData.observaciones || formData.observaciones.trim() === '') {
        errores.observaciones = 'Debe especificar el tipo de demandante en el campo Observaciones';
      }
    }

    // 3. Validación de Juzgado = Otros
    if (parseInt(formData.juzgado) === CONSTANT_IDS.JUZGADO.OTROS) {
      if (!formData.observaciones || formData.observaciones.trim() === '') {
        errores.observaciones = 'Debe especificar el juzgado donde se presentó la demanda en el campo Observaciones';
      }
    }

    // 4. Validación de Tipo de Cambio para moneda extranjera
    if (formData.moneda && parseInt(formData.moneda) !== CONSTANT_IDS.MONEDA.LEMPIRAS) {
      if (!formData.tipocambio || parseFloat(formData.tipocambio) <= 0) {
        errores.tipocambio = 'El tipo de cambio es obligatorio para moneda extranjera';
      }
    }

    // 5. Validación de montos no negativos
    const montos = ['montoestimacion', 'montoprovision', 'montogastoslegales'];
    montos.forEach(campo => {
      const valor = parseFloat(formData[campo]) || 0;
      if (valor < 0) {
        errores[campo] = 'El monto no puede ser negativo';
      }
    });

    // 6. Validación de cuentas contables según montos
    if (parseFloat(formData.montoestimacion) > 0) {
      if (!formData.cuentaestimacion || formData.cuentaestimacion.trim() === '') {
        errores.cuentaestimacion = 'Cuenta contable de estimación es obligatoria cuando el monto es mayor a 0. Ingrese una cuenta válida del catálogo contable vigente que pertenezca al activo.';
      }
    } else {
      if (formData.cuentaestimacion && formData.cuentaestimacion.trim() !== '') {
        errores.cuentaestimacion = 'La cuenta contable de estimación debe permanecer vacía cuando el monto es 0.00';
      }
    }

    if (parseFloat(formData.cuentaprovision) > 0) {
      if (!formData.cuentaprovision || formData.cuentaprovision.trim() === '') {
        errores.cuentaprovision = 'Cuenta contable de provisión es obligatoria cuando el monto es mayor a 0. Ingrese una cuenta válida del catálogo contable vigente que pertenezca al pasivo.';
      }
    } else {
      if (formData.cuentaprovision && formData.cuentaprovision.trim() !== '') {
        errores.cuentaprovision = 'La cuenta contable de provisión debe permanecer vacía cuando el monto es 0.00';
      }
    }

    if (parseFloat(formData.montogastoslegales) > 0) {
      if (!formData.cuentagastos || formData.cuentagastos.trim() === '') {
        errores.cuentagastos = 'Cuenta contable de gastos legales es obligatoria cuando el monto es mayor a 0. Ingrese una cuenta válida del catálogo contable vigente que pertenezca al pasivo o a gastos.';
      }
    } else {
      if (formData.cuentagastos && formData.cuentagastos.trim() !== '') {
        errores.cuentagastos = 'La cuenta contable de gastos legales debe permanecer vacía cuando el monto es 0.00';
      }
    }

    // 7. Validación de campos según naturaleza
    if (parseInt(formData.naturaleza) === CONSTANT_IDS.NATURALEZA.A_FAVOR) {
      if (!formData.nombredemandado || formData.nombredemandado.trim() === '') {
        errores.nombredemandado = 'Nombre del demandado es obligatorio para demandas A Favor';
      }
    } else if (parseInt(formData.naturaleza) === CONSTANT_IDS.NATURALEZA.EN_CONTRA) {
      if (!formData.nombredemandante || formData.nombredemandante.trim() === '') {
        errores.nombredemandante = 'Nombre del demandante es obligatorio para demandas En Contra';
      }
    }

    // 8. VALIDACIÓN ESPECÍFICA PARA MONTOESTIMACION SEGÚN NATURALEZA
    if (parseInt(formData.naturaleza) === CONSTANT_IDS.NATURALEZA.A_FAVOR) {
        if (!formData.montoestimacion || formData.montoestimacion.toString().trim() === '') {
            errores.montoestimacion = 'El monto de estimación es obligatorio para demandas a favor';
        } else {
            const monto = parseFloat(formData.montoestimacion);
            if (isNaN(monto) || monto < 0) {
                errores.montoestimacion = 'El monto debe ser un número mayor o igual a 0';
            }
            
            const [entero, decimal] = formData.montoestimacion.toString().split('.');
            if (entero && entero.length > 14) {
                errores.montoestimacion = 'La parte entera no puede tener más de 14 dígitos';
            }
            if (decimal && decimal.length > 2) {
                errores.montoestimacion = 'Los decimales no pueden tener más de 2 dígitos';
            }
            
            if (monto === 0) {
                errores.montoestimacion = 'El monto de estimación no puede ser 0.00 para demandas a favor';
            }
        }
        } else if (parseInt(formData.naturaleza) === CONSTANT_IDS.NATURALEZA.EN_CONTRA) {
        const monto = parseFloat(formData.montoestimacion) || 0;
        if (monto !== 0) {
            errores.montoestimacion = 'Para demandas en contra, el monto de estimación debe ser 0.00';
        }
    }

    setFormErrors(errores);
    return Object.keys(errores).length === 0;
  };

  const getFieldLabel = (fieldName) => {
    const field = apiData.fields.find(f => f.name === fieldName);
    return field?.ui?.label || fieldName;
  };

  const getCatalogOptions = (catalogName) => {
  if (catalogName === 'procuradores') {
    return procuradores
      .map(p => ({ id: p.id.toString(), label: p.nombre, codigo: p.codigo?.toString() || '' }))
      .sort((a, b) => parseInt(a.id) - parseInt(b.id));
  }
 
  return (apiData.catalogs[catalogName] || [])
    .slice()
    .map(opt => ({
      ...opt,
      id: opt.id.toString(),
      codigo: opt.codigo !== undefined ? String(opt.codigo) : ''
    }))
    .sort((a, b) => parseInt(a.id) - parseInt(b.id));
};

  const getFieldTooltip = (fieldName) => {
    return fieldTooltips[fieldName] || `Información sobre ${getFieldLabel(fieldName)}`;
  };

  // Componente para el ícono de información con Tooltip
  const InfoIconWithTooltip = ({ fieldName }) => (
    <Tooltip title={getFieldTooltip(fieldName)} arrow placement="top">
      <InfoOutlinedIcon 
        color="action" 
        sx={{ 
          fontSize: 18, 
          ml: 1,
          cursor: 'help',
          verticalAlign: 'middle'
        }} 
      />
    </Tooltip>
  );

  // Función para actualizar la visibilidad de la demanda
  const actualizarVisibilidad = async (demandaId) => {
    try {
      const token = sessionData.token;
      const responsableVisiblePara = formData.responsablevisiblepara || '1';
      
      const response = await fetch(`https://bei360.injupemp.gob.hn:8444/api/tramite/${demandaId}/visibilidad/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          responsablevisiblepara: parseInt(responsableVisiblePara)
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error al actualizar visibilidad:', errorText);
        throw new Error('Error al actualizar la visibilidad de la demanda');
      }

      console.log('Visibilidad actualizada correctamente');
      return true;
    } catch (err) {
      console.error('Error en PATCH de visibilidad:', err);
      throw err;
    }
  };

  const renderField = (field) => {
    // No renderizar campos ocultos
    if (CAMPOS_OCULTOS.includes(field.name)) {
      return null;
    }

    if (field.ui?.widget === 'hidden') {
      return null;
    }

    const value = formData[field.name] || '';
    const error = formErrors[field.name];
    const isLocked = lockedFields[field.name];
    
    const commonProps = {
      fullWidth: true,
      label: (
        <Box component="span" display="flex" alignItems="center">
          {field.ui?.label || field.name}
          <InfoIconWithTooltip fieldName={field.name} />
        </Box>
      ),
      name: field.name,
      value: value,
      onChange: handleInputChange,
      error: !!error,
      helperText: error || field.ui?.help || ' ',
      disabled: isLocked || field.ui?.widget === 'readonly',
      required: field.required && !isLocked && field.ui?.widget !== 'readonly'
    };

    // CASO ESPECIAL: Autocomplete para demandados y demandantes
    if (field.name === 'nombredemandado' || field.name === 'nombredemandante') {
      return (
        <Autocomplete
          key={field.name}
          freeSolo
          options={participantes}
          getOptionLabel={(option) => typeof option === 'string' ? option : option.NombreParte || ''}
          value={value || ''}
          onChange={(event, newValue) => handleAutocompleteChange(event, newValue, field.name)}
          onInputChange={(event, newInputValue) => {
            setSearchTerm(newInputValue);
          }}
          loading={loadingParticipantes}
          disabled={isLocked}
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.ui?.label || field.name}
              variant="outlined"
              fullWidth
              error={!!error}
              helperText={error || field.ui?.help || ' '}
              required={field.required && !isLocked}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingParticipantes ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      );
    }

    // Campo especial para responsablevisiblepara
    if (field.name === 'responsablevisiblepara') {
      return (
        <FormControl fullWidth error={!!error} required={field.required && !isLocked}>
          <InputLabel>
            <Box component="span" display="flex" alignItems="center">
              {field.ui?.label || 'Enviar a'}
              <InfoIconWithTooltip fieldName={field.name} />
            </Box>
          </InputLabel>
          <Select
            value={value || '1'}
            onChange={handleInputChange}
            label={field.ui?.label || 'Enviar a'}
            name={field.name}
          >
            {OPCIONES_VISIBILIDAD.map((opcion) => (
              <MenuItem key={opcion.id} value={opcion.id.toString()}>
                {opcion.label}
              </MenuItem>
            ))}
          </Select>
          {(error || field.ui?.help) && (
            <FormHelperText>{error || field.ui?.help || ' '}</FormHelperText>
          )}
        </FormControl>
      );
    }

    switch (field.type) {
      case 'fk':
        const options = getCatalogOptions(field.catalog);
        return (
          <FormControl 
            fullWidth 
            error={!!error}
            disabled={isLocked || field.ui?.widget === 'readonly' || (field.catalog === 'procuradores' && loading.procuradores)}
            required={field.required && !isLocked && field.ui?.widget !== 'readonly'}
          >
            <InputLabel>
              <Box component="span" display="flex" alignItems="center">
                {commonProps.label}
                {field.catalog === 'procuradores' && loading.procuradores && (
                  <CircularProgress size={16} sx={{ ml: 1 }} />
                )}
              </Box>
            </InputLabel>
            <Select
              value={value}
              onChange={handleInputChange}
              label={field.ui?.label || field.name}
              name={field.name}
            >
              <MenuItem value=""><em>Seleccione...</em></MenuItem>
              {options.map(option => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {(error || field.ui?.help) && (
              <FormHelperText>{commonProps.helperText}</FormHelperText>
            )}
          </FormControl>
        );

      case 'date':
        return (
          <TextField
            {...commonProps}
            type="date"
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'datetime':
        return (
          <TextField
            {...commonProps}
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'decimal':
        return (
          <TextField
            {...commonProps}
            type="number"
            inputProps={{
              step: field.ui?.step || "0.01",
              min: field.min || 0
            }}
          />
        );

      case 'int':
        return (
          <TextField
            {...commonProps}
            type="number"
          />
        );

      case 'string':
      default:
        return (
          <TextField
            {...commonProps}
            type="text"
            inputProps={{
              maxLength: field.maxLength
            }}
          />
        );
    }
  };

  const uploadAttachments = async (demandaId) => {
    try {
      const token = sessionData.token;
      const usuarioNombre = `${sessi.sessionData.nombre || ''} ${sessi.sessionData.apellido || ''}`.trim();
      setUploadProgress({ total: attachmentMetadata.length, completed: 0 });
      const uploadedFilesList = [];

      for (const attachment of attachmentMetadata) {
        const formData = new FormData();
        formData.append('archivo', attachment.file);
        formData.append('entity_id', demandaId);
        formData.append('usuario_id', sessionData.id);
        formData.append('usuario_nombre', usuarioNombre || sessi.sessionData.username);
    
        const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/adjuntos/subir/', {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Error al subir el archivo");
        }

        const result = await response.json();
        uploadedFilesList.push(result);
        setUploadProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
      }

      return uploadedFilesList;
    } catch (error) {
      console.error("Error al subir archivos:", error);
      throw new Error("Error al subir archivos: " + error.message);
    } finally {
      setUploadProgress(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(prev => ({ ...prev, submitting: true }));

    if (!validarCampos()) {
      setError('Hay campos inválidos. Por favor verifica los datos.');
      setLoading(prev => ({ ...prev, submitting: false }));
      return;
    }

    try {
      const requestData = {
        usuariocreadorid: parseInt(formData.usuariocreadorid),
        usuarioprocuradorid: parseInt(formData.usuarioprocuradorid),
        procuradorasignado: formData.procuradorasignado || '',
        // CAMPOS OCULTOS PERO ENVIADOS
        sucursal: parseInt(formData.sucursal) || null,
        numerodemanda: formData.numerodemanda || '',
        estado: formData.estado || 'Creada',
        // FIN CAMPOS OCULTOS
        naturaleza: parseInt(formData.naturaleza) || null,
        tipodemandante: parseInt(formData.tipodemandante) || null,
        numeroexpediente: formData.numeroexpediente || '',
        fechapresentacion: formData.fechapresentacion || null,
        nombredemandado: formData.nombredemandado || '',
        nombredemandante: formData.nombredemandante || '',
        tipodemanda: parseInt(formData.tipodemanda) || null,
        juzgado: parseInt(formData.juzgado) || null,
        moneda: parseInt(formData.moneda) || null,
        tipocambio: formData.tipocambio ? parseFloat(formData.tipocambio) : null,
        montoestimacion: formData.montoestimacion ? 
        parseFloat(formData.montoestimacion) : 
        (parseInt(formData.naturaleza) === CONSTANT_IDS.NATURALEZA.EN_CONTRA ? 0.00 : null),
        montoprovision: formData.montoprovision ? parseFloat(formData.montoprovision) : null,
        montogastoslegales: formData.montogastoslegales ? parseFloat(formData.montogastoslegales) : null,
        cuentaestimacion: formData.cuentaestimacion || '',
        cuentaprovision: formData.cuentaprovision || '',
        cuentagastos: formData.cuentagastos || '',
        riesgocredito: formData.riesgocredito ? parseInt(formData.riesgocredito) : null,
        observaciones: formData.observaciones || '',
        motivodemanda: formData.motivodemanda || '',
        accioninstitucion: formData.accioninstitucion || '',
        fechamaximacontestacion: formData.fechamaximacontestacion || null,
      };

      console.log('Enviando datos:', requestData);

      const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/tramite/crear/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        let errorMessage = 'Error al crear la demanda';
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.detail || errorMessage;
          
          if (errorData.errors) {
            const validationErrors = {};
            Object.keys(errorData.errors).forEach(field => {
              validationErrors[field] = errorData.errors[field].join(', ');
            });
            setFormErrors(validationErrors);
            errorMessage = 'Errores de validación en el formulario';
          }
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      const demandaCreada = await response.json();
      console.log('Demanda creada:', demandaCreada);

      // Paso 2: Actualizar la visibilidad usando PATCH
      try {
        await actualizarVisibilidad(demandaCreada.id);
        console.log('Visibilidad actualizada exitosamente');
      } catch (visibilityError) {
        console.error('Error al actualizar visibilidad:', visibilityError);
        // No bloqueamos el flujo si falla la actualización de visibilidad
        setError(`Demanda creada pero hubo un error al actualizar la visibilidad: ${visibilityError.message}`);
      }

      // Paso 3: Subir archivos adjuntos si existen
      if (attachmentMetadata.length > 0) {
        const files = await uploadAttachments(demandaCreada.id);
        setUploadedFiles(files);
      }

      setSuccess(true);
      resetForm();

    } catch (err) {
      console.error('Error al crear demanda:', err);
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, submitting: false }));
    }
  };

  const handleCloseNotification = () => {
    setError(null);
    setSuccess(false);
  };

  const resetForm = () => {
    const initialData = {};
    const initialLocked = {};
    
    apiData.fields.forEach(field => {
      if (field.default === 'now') {
        if (field.type === 'datetime') {
          initialData[field.name] = new Date().toISOString();
        } else if (field.type === 'date') {
          initialData[field.name] = new Date().toISOString().split('T')[0];
        }
      } else if (field.default) {
        initialData[field.name] = field.default;
      } else {
        initialData[field.name] = '';
      }

      if (field.ui?.widget === 'hidden') {
        initialLocked[field.name] = true;
      }
    });
    
    // VALORES PREDETERMINADOS PARA CAMPOS OCULTOS
    initialData.usuariocreadorid = sessionData.id;
    initialLocked.usuariocreadorid = true;
    initialData.sucursal = CONSTANT_IDS.SUCURSAL.CORTES.toString();
    initialData.moneda = CONSTANT_IDS.MONEDA.LEMPIRAS.toString();
    initialLocked.sucursal = true;
    initialData.montoestimacion = '0.00';
    initialLocked.montoestimacion = false;
    initialData.estado = 'Creada';
    initialData.numerodemanda = '';
    initialData.responsablevisiblepara = '1';
    
    setFormData(initialData);
    setLockedFields(initialLocked);
    setAttachmentMetadata([]);
    setFormErrors({});
    setSearchTerm('');
    setParticipantes([]);
  };

  if (loading.api) {
    return (
      <Container maxWidth="md" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Preparar los campos para renderizar
  const allFields = [
    ...apiData.fields,
    ...(!apiData.fields.find(f => f.name === "responsablevisiblepara")
      ? [{
          name: "responsablevisiblepara",
          ui: { label: "Enviar a" },
          type: "string",
          required: true
        }]
      : [])
  ].filter(field => !CAMPOS_OCULTOS.includes(field.name));

  return (
    <Container maxWidth="lg" style={{ marginTop: '2rem' }}>
      <Paper elevation={3} style={{ padding: '2rem', backgroundColor: '#F8F8FF' }}>
        <Typography variant="h4" align="center" gutterBottom>
          Crear Nueva Demanda
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Primera fila con campos importantes */}
            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "naturaleza") || { name: "naturaleza" })}
            </Grid>
            
            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "responsablevisiblepara") || { name: "responsablevisiblepara" })}
            </Grid>

            {/* Segunda fila */}
            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "numeroexpediente") || { name: "numeroexpediente" })}
            </Grid>
            
            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "fechapresentacion") || { name: "fechapresentacion" })}
            </Grid>

            {/* Campos dinámicos según naturaleza */}
            {parseInt(formData.naturaleza) === CONSTANT_IDS.NATURALEZA.A_FAVOR ? (
              <Grid item xs={12} sm={6}>
                {renderField(allFields.find(field => field.name === "nombredemandado") || { name: "nombredemandado" })}
              </Grid>
            ) : (
              <Grid item xs={12} sm={6}>
                {renderField(allFields.find(field => field.name === "nombredemandante") || { name: "nombredemandante" })}
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "tipodemandante") || { name: "tipodemandante" })}
            </Grid>

            {/* Tercera fila */}
            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "tipodemanda") || { name: "tipodemanda" })}
            </Grid>
            
            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "juzgado") || { name: "juzgado" })}
            </Grid>

            {/* Cuarta fila - Campos monetarios */}
            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "moneda") || { name: "moneda" })}
            </Grid>
            
            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "tipocambio") || { name: "tipocambio" })}
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "montoestimacion") || { name: "montoestimacion" })}
            </Grid>

            {/* Campos de cuentas contables */}
            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "cuentaestimacion") || { name: "cuentaestimacion" })}
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "cuentaprovision") || { name: "cuentaprovision" })}
            </Grid>

            {/* Campos adicionales */}
            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "riesgocredito") || { name: "riesgocredito" })}
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "usuarioprocuradorid") || { name: "usuarioprocuradorid" })}
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "fechamaximacontestacion") || { name: "fechamaximacontestacion" })}
            </Grid>

            {/* Campos de texto largos */}
            <Grid item xs={12} sm={6} >
              {renderField(allFields.find(field => field.name === "motivodemanda") || { name: "motivodemanda" })}
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderField(allFields.find(field => field.name === "observaciones") || { name: "observaciones" })}
            </Grid>

            {/* Sección de archivos adjuntos */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" mb={2}>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 0, mr: 1 }}>
                  Adjuntos
                </Typography>
                <Tooltip title="Puede adjuntar documentos relacionados con la demanda como PDF, Word, Excel o imágenes. Tamaño máximo por archivo: 10MB." arrow>
                  <InfoOutlinedIcon color="action" sx={{ fontSize: 18, cursor: 'help' }} />
                </Tooltip>
              </Box>
              <input
                accept={CONSTANTS.ALLOWED_FILE_TYPES.join(',')}
                style={{ display: 'none' }}
                id="raised-button-file"
                multiple
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="raised-button-file">
                <Button
                  variant="contained"
                  color="success"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                >
                  Subir Archivos
                </Button>
              </label>

              {uploadProgress && (
                <Box mt={2}>
                  <Typography variant="body2">
                    Subiendo archivos: {uploadProgress.completed} de {uploadProgress.total}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(uploadProgress.completed / uploadProgress.total) * 100}
                  />
                </Box>
              )}

              {attachmentMetadata.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2">
                    Archivos seleccionados:
                  </Typography>
                  <List dense>
                    {attachmentMetadata.map((file, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CloudUploadIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={`${(file.size / 1024).toFixed(2)} KB`}
                        />
                        <Tooltip title="Eliminar archivo" arrow>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveAttachment(index)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Grid>

            {/* Botones de acción */}
            <Grid item xs={12} container justifyContent="flex-end" spacing={2}>
              <Grid item>
                <Tooltip title="Limpiar todos los campos del formulario" arrow>
                  <Button
                    color="error"
                    onClick={resetForm}
                    disabled={loading.submitting}
                  >
                    Cancelar
                  </Button>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title="Guardar la demanda con los datos ingresados" arrow>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading.submitting || (uploadProgress && uploadProgress.completed < uploadProgress.total)}
                    startIcon={loading.submitting ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {loading.submitting ? 'Guardando...' : 'Guardar Demanda'}
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Notificaciones */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={handleCloseNotification}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={handleCloseNotification}
          sx={{ maxWidth: 600 }}
        >
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              ¡Éxito! La demanda ha sido creada correctamente.
            </Typography>
            {uploadedFiles.length > 0 && (
              <Typography variant="body2">
                {uploadedFiles.length} archivo(s) subido(s) correctamente
              </Typography>
            )}
          </Box>
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CrearDemanda;